import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MerchantService } from 'src/app/services/merchant/merchant.service';
import { CashRegisterService } from 'src/app/services/cash-register/cash-register.service';
import { lastValueFrom } from 'rxjs';
import Swal from 'sweetalert2';

/**
 * EL ALTA DE UN USUARIO DEL COMERCIO — página con ruta propia, a todo el ancho.
 *
 * 🔴 Era un diálogo dentro de la ficha del comercio y dejó de alcanzar el día
 * que el alta pasó a tener DOS caminos: una persona lleva nombre, apellido y
 * correo; una caja lleva tienda, caja, usuario y clave con confirmación. Eso no
 * entra en un modal — y encima un modal no tiene dirección: no se comparte, no
 * vuelve con el botón de atrás y recargar la página lo pierde.
 *
 * Decisión de Juan (2026-09-19): «igual que la creación de usuario, que tiene
 * que ser un componente. Entiende que no me gustan los modales».
 *
 * 📍 Mismo patrón que `ComerciosTiendaComponent`: vive bajo el comercio
 * (`/console/merchants/:code/usuarios/nuevo`), con miga de pan y un «volver»
 * que devuelve a la pestaña —y al bloque— de donde se salió.
 *
 * ⚠️ NO trae endpoints nuevos: el comercio y sus tiendas salen de
 * `GET /api/merchant/:code`, las cajas de `CashRegisterService`, y el alta de
 * `POST /api/merchant/:code/usuarios`, que es el mismo de antes.
 */
@Component({
  selector: 'app-comercios-usuario-nuevo',
  templateUrl: './comercios-usuario-nuevo.component.html'
})
export class ComerciosUsuarioNuevoComponent implements OnInit {

  codigo = '';
  comercio: any = null;
  tiendas: any[] = [];

  cargando = true;
  noEncontrado = false;

  /** Los que ya existen. Sólo para avisar de un nombre repetido antes de mandar. */
  usuarios: any[] = [];
  /**
   * ⚠️ Puede fallar sin que el alta deje de funcionar: la lista es para AVISAR
   * de un repetido, no para autorizar. Si no vino, se dice —no se calla ni se
   * afirma que el nombre está libre, porque eso no se puede saber.
   */
  listaDisponible = false;

  /**
   * 🔴 Nace SIN tipo, y lo primero que se pregunta es qué se está dando de alta.
   * Los dos caminos no comparten casi ningún campo: a una caja no se le pide
   * correo y a una persona no se le pide tienda. Un formulario único con la
   * mitad de los campos apagados es el que termina creando cajas con el correo
   * de quien las creó.
   */
  tipo: 'PERSONA' | 'CAJA' | null = null;

  /**
   * 🔴 El papel de la persona (Juan, 2026-10-08: «¿dónde está la selección del rol gerente?»). Hasta
   * ese día la consola daba de alta SIEMPRE un administrador; el gerente y el ejecutivo de ventas sólo
   * los podía crear el comercio desde su portal. Son los mismos tres que ofrece el portal.
   */
  rolPersona: 'ADMIN' | 'GERENTE' | 'VENTAS' = 'ADMIN';

  /**
   * El ejecutivo de ventas vende por link de pago: se ofrece sólo si el comercio vende por ahí. Un
   * comercio de antes de los canales (sin `canales`) no dice por dónde vende, y ahí no se esconde.
   */
  get vendePorLink(): boolean {
    const canales = this.comercio?.canales;
    return !Array.isArray(canales) || canales.includes('LINK_PAGO');
  }

  readonly ROLES_PERSONA = [
    { valor: 'ADMIN' as const,   titulo: 'Administrador',        detalle: 'Ve el comercio entero y gestiona los usuarios.' },
    { valor: 'GERENTE' as const, titulo: 'Gerente',              detalle: 'Autoriza: da el código que la caja pide para abrir su turno o anular una orden.' },
    { valor: 'VENTAS' as const,  titulo: 'Ejecutivo de ventas',  detalle: 'Vende sin mostrador: manda la compra al teléfono del cliente. No cobra.' },
  ];

  get rolesPersona() {
    return this.ROLES_PERSONA.filter(r => r.valor !== 'VENTAS' || this.vendePorLink);
  }

  form: any = {
    name: '', lastName: '', email: '',
    sufijo: '', sufijoTocado: false,
    shop: '', cajaId: '',
    clave: '', clave2: '', verClave: false
  };

  guardando = false;

  // Las cajas de la tienda elegida, traídas de su propio módulo.
  cajasDeLaTienda: any[] = [];
  cargandoCajasTienda = false;
  errorCajasTienda = '';

  constructor(
    private ruta: ActivatedRoute,
    private router: Router,
    private _merchant: MerchantService,
    private _cajas: CashRegisterService
  ) { }

  ngOnInit(): void {
    this.ruta.paramMap.subscribe(p => {
      this.codigo = p.get('code') || '';
      this.cargar();
    });
  }

  /**
   * Una sola llamada trae el nombre del comercio —que hace falta para la miga
   * de pan— y sus tiendas, que son el desplegable del camino de la caja.
   */
  cargar = async (): Promise<void> => {
    this.cargando = true;
    this.noEncontrado = false;
    try {
      const rep = await this._merchant.getMerchant(this.codigo);
      this.comercio = rep?.merchant || null;
      this.tiendas = rep?.shops || [];
      this.noEncontrado = !this.comercio;
    } catch {
      this.comercio = null;
      this.tiendas = [];
      this.noEncontrado = true;
    } finally {
      this.cargando = false;
    }
    if (this.comercio) this.cargarUsuarios();
  };

  private cargarUsuarios = async (): Promise<void> => {
    try {
      const r = await this._merchant.getMerchantUsers(this.codigo);
      this.usuarios = r?.usuarios || [];
      this.listaDisponible = true;
    } catch {
      this.usuarios = [];
      this.listaDisponible = false;
    }
  };

  // ── ir y volver ───────────────────────────────────────────────────────────

  /**
   * Vuelve a la pantalla de Usuarios del comercio.
   *
   * 🔴 ANTES IBA A LA FICHA CON `?tab=usuarios&sub=…`, y desde el reparto del
   * 2026-09-20 eso aterriza en el lugar equivocado: la ficha ya no lee
   * `queryParams` —se le sacaron las siete solapas— y Usuarios es una ruta
   * propia. O sea que dabas de alta un usuario y caias en la ficha del comercio
   * SIN VER el usuario que acababas de crear. No fallaba nada: te llevaba a otro
   * lado, que es la clase de defecto que no da sintoma.
   *
   * El `sub` se conserva porque la pantalla nueva tiene las dos listas —personas
   * y cajas— y asi se llega con la que corresponde ya buscada.
   */
  volver = (sub: 'usuarios' | 'cajas' = 'usuarios'): void => {
    this.router.navigate(['/console/merchants', this.codigo, 'usuarios'],
                         { queryParams: sub === 'cajas' ? { ver: 'cajas' } : {} });
  };

  /** Al bloque que corresponde a lo que se está dando de alta ahora mismo. */
  cancelar = (): void => {
    if (this.guardando) return;
    this.volver(this.tipo === 'CAJA' ? 'cajas' : 'usuarios');
  };

  /** A la ficha de la tienda, que es donde se crea una caja que falta. */
  irALaTienda = (code: string): void => {
    this.router.navigate(['/console/merchants', this.codigo, 'tiendas', code]);
  };

  elegirTipo = (t: 'PERSONA' | 'CAJA'): void => {
    this.tipo = t;
  };

  // ── el nombre de usuario ──────────────────────────────────────────────────

  /**
   * El prefijo: el acrónimo del comercio, en minúscula.
   *
   * 🔴 Va FIJO y no se puede editar. Viendo un nombre de usuario suelto —en un
   * log, en un reporte, en un ticket— tiene que saberse de qué comercio es sin
   * ir a buscarlo. Si el prefijo fuera libre, esa garantía se pierde el primer
   * día que alguien escriba otra cosa.
   */
  get prefijo(): string { return (this.codigo || '').toLowerCase(); }

  /**
   * Lo que se escribe, ordenado: minúsculas, sin acentos, y sólo letras,
   * números y puntos. Se limpia AL ESCRIBIR y no al guardar, para que se vea
   * exactamente el nombre que va a quedar.
   */
  private limpiar = (v: string): string =>
    (v || '').toLowerCase()
      .normalize('NFD').replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9.]/g, '');

  normalizarSufijo = (): void => {
    this.form.sufijo = this.limpiar(this.form.sufijo);
    // A partir de acá el nombre es de quien lo escribió: deja de seguir al nombre.
    this.form.sufijoTocado = true;
  };

  /**
   * 🔴 El nombre de usuario SIGUE AL NOMBRE mientras nadie lo toque.
   *
   * Escribiendo «Angela» aparece `mt.angela` solo, y se ve antes de guardar.
   * El nombre de usuario se dicta por teléfono y se busca en una lista, así que
   * tiene que parecerse a la persona.
   */
  alEscribirNombre = (): void => {
    if (this.form.sufijoTocado) return;
    const primero = String(this.form.name || '').trim().split(/\s+/)[0];
    this.form.sufijo = this.limpiar(primero);
  };

  get usuarioFinal(): string {
    const suf = (this.form.sufijo || '').trim();
    return suf ? `${this.prefijo}.${suf}` : '';
  }

  /**
   * ¿Ese nombre ya está tomado?
   *
   * ⚠️ Sólo se puede responder si la lista vino. Sin lista esto devuelve false
   * —no bloquea— y la pantalla avisa que no se pudo comprobar. Un «está libre»
   * afirmado sin haber mirado es peor que no decir nada.
   */
  get usuarioRepetido(): boolean {
    if (!this.listaDisponible) return false;
    const u = this.usuarioFinal;
    return !!u && this.usuarios.some(x => x.username === u);
  }

  // ── el camino de la PERSONA ───────────────────────────────────────────────

  get personaValida(): boolean {
    const nombre = String(this.form.name || '').trim();
    const correo = String(this.form.email || '').trim();
    const correoOk = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(correo);
    // El sufijo es OPCIONAL: si va vacío, el servidor lo deriva.
    return !!(nombre && correoOk) && !this.usuarioRepetido;
  }

  // ══════════════════════════════════════════════════════════════════════════
  // EL CAMINO DE LA CAJA
  // ══════════════════════════════════════════════════════════════════════════
  //
  // 🔴 SON TRES COSAS DISTINTAS, y confundirlas es el error que hay que evitar:
  //
  //   1. LA CAJA       — una entidad de la tienda. Tiene número, afiliación y
  //                      tipo, y existe aunque nadie la opere. Se crea en la
  //                      ficha de la tienda, no acá.
  //   2. EL USUARIO    — una credencial: nombre de entrada y clave.
  //   3. LA ASOCIACIÓN — esta pantalla. Un usuario queda atado a UNA caja.
  //
  // Y lo que viaja estampado en la venta es LA CAJA (tienda + número), nunca el
  // nombre del usuario. Un usuario llamado `mt.caja1` no es la caja 1: es una
  // credencial que casualmente se llama así.

  /** Las tiendas del desplegable. Las inhabilitadas no operan. */
  get tiendasParaCaja(): any[] {
    return this.tiendas.filter(t => t.status !== false);
  }

  /**
   * Al elegir la tienda se traen SUS cajas. No se teclea un número: se elige
   * una caja que ya existe, porque la caja es de la tienda y ya fue dada de
   * alta ahí. Tecleando el número se podría asociar un usuario a una caja
   * inexistente, y eso recién se vería el día de la venta.
   */
  alElegirTienda = async (): Promise<void> => {
    this.form.cajaId = '';
    this.cajasDeLaTienda = [];
    this.errorCajasTienda = '';
    const code = this.form.shop;
    if (!code) return;
    this.cargandoCajasTienda = true;
    try {
      const r: any = await lastValueFrom(this._cajas.getCashRegistersByShop(code));
      this.cajasDeLaTienda = r?.cashRegisters || r?.cash_registers || r?.data || [];
    } catch (e: any) {
      // ⚠️ Un error al traer las cajas NO puede verse igual que «esta tienda no
      // tiene cajas»: uno es un problema del servidor y el otro es un dato.
      this.errorCajasTienda = e?.error?.msg || 'No se pudieron traer las cajas de esta tienda.';
    } finally {
      this.cargandoCajasTienda = false;
    }
  };

  /** ¿Se puede afirmar que la tienda elegida no tiene ninguna caja? */
  get tiendaSinCajas(): boolean {
    return !!this.form.shop && !this.cargandoCajasTienda
           && !this.errorCajasTienda && !this.cajasDeLaTienda.length;
  }

  /** Cómo se lee una caja en el desplegable: número, afiliación y tipo. */
  etiquetaCaja = (c: any): string => {
    const tipo = c?.type === 'AUTOPAGO' ? 'Autopago' : 'Normal';
    return `Caja ${c?.cash_register_number} · afiliación ${c?.afiliation_number || '—'} · ${tipo}`;
  };

  /**
   * 🔴 La clave la escribe quien da de alta, no la inventa el sistema.
   * Se pide dos veces: se dicta por teléfono y un error de tipeo se descubre
   * recién cuando el de la tienda no puede entrar.
   */
  get claveValida(): boolean {
    return String(this.form.clave || '').length >= 6;
  }

  get claveConfirmada(): boolean {
    return !!this.form.clave && this.form.clave === this.form.clave2;
  }

  get cajaValida(): boolean {
    return !!(this.form.shop && this.form.cajaId &&
              this.usuarioFinal && !this.usuarioRepetido &&
              this.claveValida && this.claveConfirmada);
  }

  get valido(): boolean {
    if (!this.tipo) return false;
    return this.tipo === 'CAJA' ? this.cajaValida : this.personaValida;
  }

  /**
   * Lo que falta para poder crear, dicho con todas las letras.
   *
   * 📍 Rediseño 2026-09-20: los dos caminos tenían su propio pie de tarjeta con
   * el botón desactivado y ni una palabra de por qué. Quedó una sola barra al
   * pie, y la barra nombra el campo que falta en vez de dejar adivinando.
   */
  get faltaParaCrear(): string {
    if (this.usuarioRepetido) return 'Ese nombre de usuario ya existe en este comercio';
    if (this.tipo === 'CAJA') {
      if (!this.tiendasParaCaja.length) return 'Este comercio no tiene tiendas habilitadas';
      if (!this.form.shop) return 'Falta elegir la tienda';
      if (!this.form.cajaId) return 'Falta elegir la caja';
      if (!this.usuarioFinal) return 'Falta el nombre de usuario';
      if (!this.claveValida) return 'La clave tiene que tener seis caracteres o más';
      if (!this.claveConfirmada) return 'Las dos claves no son iguales';
      return '';
    }
    if (!String(this.form.name || '').trim()) return 'Falta el nombre';
    if (!this.personaValida) return 'Falta el correo, o el que hay no es válido';
    return '';
  }

  // ── el alta ───────────────────────────────────────────────────────────────

  crear = async (): Promise<void> => {
    if (!this.valido || this.guardando) return;
    const esCaja = this.tipo === 'CAJA';
    this.guardando = true;
    try {
      const cuerpo = esCaja
        ? {
            rol: 'CAJA',
            username: this.usuarioFinal,
            password: this.form.clave,
            shop: this.form.shop,             // el CÓDIGO de la tienda
            cash_register_id: this.form.cajaId // el _id de la caja elegida
          }
        : {
            rol: this.rolPersona,
            name: String(this.form.name).trim(),
            lastName: String(this.form.lastName || '').trim(),
            email: String(this.form.email).trim().toLowerCase(),
            // Si va vacío, el servidor lo deriva. No se manda una cadena vacía:
            // el back distingue «no me lo mandaron» de «me mandaron nada».
            ...(this.usuarioFinal ? { username: this.usuarioFinal } : {})
          };
      const r = await this._merchant.createMerchantUser(this.codigo, cuerpo);
      const entra = r?.usuario?.username || this.usuarioFinal;
      this.guardando = false;
      if (r?.clave_inicial) {
        // 🔴 Sin temporizador: es el único momento en que alguien ve con qué
        // entra este usuario, y hay que anotarlo o dictarlo.
        await this.mostrarAcceso(entra, r.clave_inicial);
      } else {
        // La clave la puso quien dio de alta: no hay nada que revelar.
        await Swal.fire({
          icon: 'success', title: 'Ya puede entrar',
          html: `<p style="font-size:1.1rem;margin:.75rem 0"><strong>${entra}</strong></p>
                 <p style="font-size:.85rem">Entra con la clave que le pusiste.</p>`,
          confirmButtonText: 'Listo'
        });
      }
      this.volver(esCaja ? 'cajas' : 'usuarios');
    } catch (e: any) {
      // ⚠️ La pantalla NO se abandona: lo que se escribió se queda, para
      // corregir y reintentar.
      Swal.fire({ icon: 'error',
                  title: esCaja ? 'No se pudo crear el usuario de la caja'
                                : 'No se pudo crear el usuario',
                  text: e?.error?.msg || e?.message || 'Intenta de nuevo.',
                  footer: e?.error?.detalle || '' });
    } finally {
      this.guardando = false;
    }
  };

  /**
   * ⚠️ `navigator.clipboard` NO existe fuera de un contexto seguro: si la
   * consola se abre por IP en http —que es como la abren en las tiendas— es
   * `undefined` y el botón no haría absolutamente nada, sin error. De ahí el
   * respaldo con el textarea.
   */
  private alPortapapeles = async (texto: string): Promise<boolean> => {
    try {
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(texto);
        return true;
      }
    } catch { /* sigue por el respaldo */ }
    try {
      const ta = document.createElement('textarea');
      ta.value = texto;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch { return false; }
  };

  /**
   * El cartel de acceso — el único diálogo que queda en este camino, y está
   * permitido porque muestra UN dato UNA vez.
   *
   * 🔴 Es la ÚNICA vez que esta clave se ve. No queda guardada en ningún lado
   * legible: si se pierde, se repone, no se recupera.
   */
  private mostrarAcceso = (usuario: string, clave: string): Promise<any> => {
    const u = usuario || '—';
    const c = clave || 'admin123';
    return Swal.fire({
      icon: 'success',
      title: 'Ya puede entrar',
      html: `<p style="font-size:1.25rem;margin:.75rem 0;line-height:1.7">
               <strong>${u}</strong><br><strong>${c}</strong>
             </p>
             <button type="button" id="kbCopiarAcceso" class="swal2-styled swal2-cancel">
               Copiar usuario y clave
             </button>
             <p style="font-size:.85rem;margin-top:1rem">
               Esta clave <strong>no se vuelve a mostrar</strong>. Si se pierde, se repone con la
               llave de la fila.<br>El sistema le va a exigir cambiarla al entrar.
             </p>`,
      confirmButtonText: 'Listo, ya la anoté',
      didOpen: () => {
        const boton = document.getElementById('kbCopiarAcceso');
        if (!boton) return;
        boton.addEventListener('click', async () => {
          const ok = await this.alPortapapeles(`${u} / ${c}`);
          boton.textContent = ok ? 'Copiado' : 'No se pudo copiar';
        });
      }
    });
  };
}
